// src/pages/intranet/pages/Tesoreria.jsx
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { DollarSign, TrendingUp, TrendingDown, AlertCircle, Search, Filter, X, Lock, Plus, FileText } from 'lucide-react';

export default function Tesoreria() {
  const { user } = useAuth();
  
  const currentUserId = user?.id || user?.user?.id;
  const currentUserRole = user?.role || user?.user?.role;
  const currentLogiaId = user?.logia_id || user?.user?.logia_id;

  const [activeTab, setActiveTab] = useState('balance');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGestionCuotasOpen, setIsGestionCuotasOpen] = useState(false);
  
  // Estado para el modal de pago interactivo
  const [pagoModalData, setPagoModalData] = useState(null);

  const [movimientos, setMovimientos] = useState([]);
  const [estadoCuentas, setEstadoCuentas] = useState([]);

  const getGradoNivel = (role) => {
    if (role === 'ADMIN') return 4;
    if (role === 'MAESTRO') return 3;
    if (role === 'COMPAÑERO') return 2;
    return 1;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (currentUserRole && currentLogiaId) {
      cargarMovimientos();
      cargarEstadosCuenta();
    }
  }, [currentUserRole, currentLogiaId]);

  // --- FETCHERS ---
  const cargarMovimientos = async () => {
    try {
      const res = await fetch('https://logia-production-725b.up.railway.app/api/tesoreria/movimientos');
      const data = await res.json();
      if (data.success) setMovimientos(data.data);
    } catch (error) { console.error("Error cargando balance:", error); }
  };

  const cargarEstadosCuenta = async () => {
    try {
      const nivel = getGradoNivel(currentUserRole);
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/tesoreria/estado-cuenta?grado_nivel=${nivel}&usuario_id=${currentUserId}&logia_id=${currentLogiaId}`);
      const data = await res.json();
      if (data.success) setEstadoCuentas(data.data);
    } catch (error) { console.error("Error cargando estados de cuenta:", error); }
  };

  // --- HANDLERS ---
  const handleGuardarMovimiento = async (formData) => {
    try {
      const res = await fetch('https://logia-production-725b.up.railway.app/api/tesoreria/movimientos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, usuario_id: currentUserId })
      });
      const data = await res.json();
      if (data.success) {
        setIsModalOpen(false);
        cargarMovimientos();
      } else { alert('Error: ' + data.error); }
    } catch (error) { alert('Error de conexión.'); }
  };

  const handleProcesarPago = async (payload) => {
    try {
      const res = await fetch('https://logia-production-725b.up.railway.app/api/tesoreria/pagar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, anio: new Date().getFullYear(), registrado_por: currentUserId })
      });
      const data = await res.json();
      if (data.success) {
        setPagoModalData(null);
        cargarEstadosCuenta();
        cargarMovimientos();
      } else { alert(data.error); }
    } catch (error) { alert('Error procesando el pago.'); }
  };

  const handleGenerarCuotaMasiva = async (formData) => {
    try {
      const res = await fetch('https://logia-production-725b.up.railway.app/api/tesoreria/cuotas/masivas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, anio: new Date().getFullYear() })
      });
      const data = await res.json();
      if (data.success) {
        setIsGestionCuotasOpen(false);
        cargarEstadosCuenta();
        alert(data.message);
      } else { alert(data.error); }
    } catch (error) { alert('Error de conexión.'); }
  };

  // --- KPIs ---
  const kpis = useMemo(() => {
    let ingresos = 0, egresos = 0, ingresosMes = 0, egresosMes = 0;
    const mesActual = new Date().getMonth(); 
    movimientos.forEach(mov => {
      const monto = parseFloat(mov.monto) || 0;
      const [dia, mes, anio] = mov.fecha.split('/');
      const movMes = parseInt(mes, 10) - 1;
      if (mov.tipo === 'ingreso') { ingresos += monto; if (movMes === mesActual) ingresosMes += monto; } 
      else { egresos += monto; if (movMes === mesActual) egresosMes += monto; }
    });
    return { fondoCaja: ingresos - egresos, ingresosMes, egresosMes };
  }, [movimientos]);

  return (
    <div className="flex flex-col gap-6 lg:gap-8 animate-fade-in relative">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-white m-0 tracking-wide">Tesorería y Finanzas</h1>
          <p className="text-gray-400 font-light text-sm mt-2 m-0">Gestión del patrimonio y obligaciones de la Logia</p>
        </div>
        <div className="flex flex-wrap gap-4">
          {(currentUserRole === 'MAESTRO' || currentUserRole === 'ADMIN') && (
            <>
              <button 
                className="bg-transparent border border-white/20 text-gray-400 hover:text-white hover:border-[#C5A059] px-4 py-2 text-xs tracking-widest uppercase flex items-center gap-2 transition-colors"
                onClick={() => setIsGestionCuotasOpen(true)}
              >
                <FileText size={14} /> Nueva Cuota Extra
              </button>
              <button 
                className="bg-[#C5A059] hover:bg-white text-[#050505] px-4 py-2 text-xs tracking-widest uppercase font-bold transition-colors flex items-center gap-2"
                onClick={() => setIsModalOpen(true)}
              >
                <Plus size={14} /> Nuevo Movimiento
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard title="Fondo en Caja" amount={`S/ ${kpis.fondoCaja.toFixed(2)}`} icon={<DollarSign size={20} className="text-blue-400" />} trend="Capital operativo" />
        <KpiCard title="Ingresos (Este Mes)" amount={`S/ ${kpis.ingresosMes.toFixed(2)}`} icon={<TrendingUp size={20} className="text-green-500" />} trend="Aportes recientes" />
        <KpiCard title="Egresos (Este Mes)" amount={`S/ ${kpis.egresosMes.toFixed(2)}`} icon={<TrendingDown size={20} className="text-red-500" />} trend="Gastos operativos" />
        {(currentUserRole === 'MAESTRO' || currentUserRole === 'ADMIN') ? (
           <KpiCard title="Control de Mora" amount="Auditoría" icon={<AlertCircle size={20} className="text-[#C5A059]" />} trend="Haz clic en deudas para cobrar" isWarning />
        ) : (
           <KpiCard title="Mi Deuda" amount="Por Calcular" icon={<AlertCircle size={20} className="text-[#C5A059]" />} trend="Ponte al día con Tesorería" isWarning />
        )}
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 shadow-2xl overflow-hidden relative">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>

        <div className="flex border-b border-white/10 relative z-10 bg-[#050505]/50 overflow-x-auto hide-scrollbar">
          <button className={`flex-1 py-4 px-6 text-xs md:text-sm tracking-[0.2em] uppercase transition-all duration-300 font-semibold whitespace-nowrap border-b-2 ${activeTab === 'balance' ? 'border-[#C5A059] text-[#C5A059] bg-white/5' : 'border-transparent text-gray-500 hover:text-gray-300 hover:bg-white/5'}`} onClick={() => setActiveTab('balance')}>
            Balance General
          </button>
          <button className={`flex-1 py-4 px-6 text-xs md:text-sm tracking-[0.2em] uppercase transition-all duration-300 font-semibold whitespace-nowrap border-b-2 ${activeTab === 'estados' ? 'border-[#C5A059] text-[#C5A059] bg-white/5' : 'border-transparent text-gray-500 hover:text-gray-300 hover:bg-white/5'}`} onClick={() => setActiveTab('estados')}>
            {(currentUserRole === 'MAESTRO' || currentUserRole === 'ADMIN') ? 'Estados de Cuenta (Logia)' : 'Mi Estado de Cuenta'}
          </button>
        </div>

        <div className="relative z-10">
          {activeTab === 'balance' ? (
            <BalanceGeneralTable data={movimientos} />
          ) : (
            <>
              {(currentUserRole !== 'MAESTRO' && currentUserRole !== 'ADMIN') && (
                <div className="flex items-center gap-3 bg-blue-900/10 border-b border-blue-900/30 p-4 text-blue-300 text-sm font-light">
                  <Lock size={16} /><span>Por privacidad, solo ves tu historial.</span>
                </div>
              )}
              <EstadoCuentaTable data={estadoCuentas} role={currentUserRole} onCobrar={(data) => setPagoModalData(data)} />
            </>
          )}
        </div>
      </div>

      {isModalOpen && <NuevoMovimientoModal onClose={() => setIsModalOpen(false)} onSave={handleGuardarMovimiento} />}
      {isGestionCuotasOpen && <GestionarCuotasModal onClose={() => setIsGestionCuotasOpen(false)} onSave={handleGenerarCuotaMasiva} />}
      {pagoModalData && <PagoCuotaModal data={pagoModalData} onClose={() => setPagoModalData(null)} onSave={handleProcesarPago} />}
    </div>
  );
}

// --- SUBCOMPONENTES ---

function EstadoCuentaTable({ data, role, onCobrar }) {
  const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Dic'];
  const isMaestro = role === 'MAESTRO' || role === 'ADMIN';

  return (
    <div className="overflow-x-auto pb-6">
      <table className="w-full text-left border-collapse min-w-[1200px]">
        <thead>
          <tr className="border-b border-white/10 bg-white/5">
            <th className="py-4 px-4 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold sticky left-0 bg-[#0a0a0a] z-10 border-r border-white/5 min-w-[200px]">Hermano</th>
            <th className="py-4 px-4 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold text-center border-r border-white/5">Saldo a Favor</th>
            <th className="py-4 px-2 text-[#C5A059] text-[9px] tracking-[0.2em] uppercase font-semibold text-center">Instal.</th>
            {meses.map(mes => <th key={mes} className="py-4 px-2 text-gray-400 text-[9px] tracking-[0.2em] uppercase font-semibold text-center">{mes}</th>)}
            <th className="py-4 px-2 text-[#C5A059] text-[9px] tracking-[0.2em] uppercase font-semibold text-center">Anivers.</th>
            <th className="py-4 px-2 text-[#C5A059] text-[9px] tracking-[0.2em] uppercase font-semibold text-center">Extra.</th>
          </tr>
        </thead>
        <tbody>
          {data.map((hermano) => (
            <tr key={hermano.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
              <td className="py-4 px-4 text-white font-medium text-sm sticky left-0 bg-[#0a0a0a] border-r border-white/5">
                {hermano.nombre} <span className="block mt-1 text-[9px] tracking-widest uppercase font-mono text-gray-500">{hermano.rol}</span>
              </td>
              <td className="py-4 px-4 text-center border-r border-white/5">
                <span className={`font-mono text-xs ${hermano.saldo_favor > 0 ? 'text-green-400' : 'text-gray-500'}`}>S/ {hermano.saldo_favor.toFixed(2)}</span>
              </td>
              <td className="py-4 px-2 text-center">
                <StatusBadge status={hermano.instalacion} onClick={() => isMaestro && hermano.instalacion !== 'pagado' && onCobrar({hermano, concepto: 'instalacion'})} clickable={isMaestro && hermano.instalacion !== 'pagado'} />
              </td>
              {meses.map((mes, index) => {
                const status = index < hermano.mesesPagados ? 'pagado' : 'pendiente';
                return (
                  <td key={index} className="py-4 px-2 text-center">
                    <StatusBadge status={status} onClick={() => isMaestro && status !== 'pagado' && onCobrar({hermano, concepto: 'cotizacion', mes: index + 1})} clickable={isMaestro && status !== 'pagado'} />
                  </td>
                );
              })}
              <td className="py-4 px-2 text-center">
                <StatusBadge status={hermano.aniversario} onClick={() => isMaestro && hermano.aniversario !== 'pagado' && onCobrar({hermano, concepto: 'aniversario'})} clickable={isMaestro && hermano.aniversario !== 'pagado'} />
              </td>
              <td className="py-4 px-2 text-center">
                <StatusBadge status={hermano.extra} onClick={() => isMaestro && hermano.extra !== 'pagado' && onCobrar({hermano, concepto: 'extraordinaria'})} clickable={isMaestro && hermano.extra !== 'pagado'} />
              </td>
            </tr>
          ))}
          {data.length === 0 && <tr><td colSpan="17" className="py-8 text-center text-gray-500 text-xs tracking-widest uppercase">No hay datos para mostrar.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function BalanceGeneralTable({ data }) {
  return (
    <div className="overflow-x-auto pb-6">
      <table className="w-full text-left border-collapse min-w-[800px]">
        <thead>
          <tr className="border-b border-white/10 bg-white/5">
            <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Fecha</th>
            <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Comprobante</th>
            <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Detalle de Operación</th>
            <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Categoría</th>
            <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold text-right">Monto (S/.)</th>
          </tr>
        </thead>
        <tbody>
          {data.map((mov) => (
            <tr key={mov.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
              <td className="py-4 px-6 text-gray-300 text-sm font-mono">{mov.fecha}</td>
              <td className="py-4 px-6 text-gray-500 text-xs font-mono">{mov.comprobante}</td>
              <td className="py-4 px-6 text-white text-sm font-medium">{mov.detalle}</td>
              <td className="py-4 px-6">
                <span className={`px-3 py-1 text-[9px] tracking-widest uppercase font-mono border ${mov.tipo === 'ingreso' ? 'bg-green-900/20 text-green-400 border-green-900/50' : 'bg-red-900/20 text-red-400 border-red-900/50'}`}>{mov.categoria}</span>
              </td>
              <td className={`py-4 px-6 text-right font-medium ${mov.tipo === 'ingreso' ? 'text-green-500' : 'text-red-500'}`}>{mov.tipo === 'ingreso' ? '+' : '-'} {mov.monto.toFixed(2)}</td>
            </tr>
          ))}
          {data.length === 0 && <tr><td colSpan="5" className="py-8 text-center text-gray-500 text-xs tracking-widest uppercase">No hay movimientos financieros registrados.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function KpiCard({ title, amount, icon, trend, isWarning }) {
  return (
    <div className={`bg-[#0a0a0a] border-t-2 ${isWarning ? 'border-t-[#C5A059]' : 'border-t-white/20'} border-x border-b border-white/5 p-6 shadow-lg flex flex-col gap-4`}>
      <div className="flex justify-between items-center">
        <span className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">{title}</span>
        <div className="bg-[#050505] border border-white/10 p-2 rounded-sm">{icon}</div>
      </div>
      <div className="font-serif text-3xl text-white m-0">{amount}</div>
      <div className="text-gray-500 text-xs tracking-wide">{trend}</div>
    </div>
  );
}

function StatusBadge({ status, onClick, clickable }) {
  if (status === 'pagado') {
    return <div className="bg-green-900/30 text-green-500 border border-green-800/50 py-1 px-2 text-[9px] uppercase tracking-widest mx-auto w-16 text-center font-mono">Pagado</div>;
  }
  return (
    <button onClick={clickable ? onClick : undefined} className={`bg-red-900/30 text-red-400 border border-red-800/50 py-1 px-2 text-[9px] uppercase tracking-widest mx-auto w-16 text-center font-mono ${clickable ? 'cursor-pointer hover:bg-red-800/50 transition-colors' : 'cursor-default opacity-50'}`}>
      Deuda
    </button>
  );
}

function PagoCuotaModal({ data, onClose, onSave }) {
  const cuotaAsociada = data.hermano.cuotasDetalle?.find(c => c.concepto === data.concepto && (!data.mes || c.mes === data.mes));
  const noExisteDeuda = !cuotaAsociada;
  const montoEsperadoInicial = cuotaAsociada ? parseFloat(cuotaAsociada.monto_esperado) : '';
  
  const [montoEsperado, setMontoEsperado] = useState(montoEsperadoInicial);
  const [montoPagado, setMontoPagado] = useState(montoEsperadoInicial);
  const [usarSaldo, setUsarSaldo] = useState(false);

  // Efecto para que el monto pagado se autocomplete si el Maestro define una nueva cuota
  useEffect(() => {
    if (noExisteDeuda && montoEsperado !== '') {
      setMontoPagado(montoEsperado);
    }
  }, [montoEsperado, noExisteDeuda]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ 
      usuario_id: data.hermano.id, 
      concepto: data.concepto, 
      mes: data.mes, 
      monto_pagado: parseFloat(montoPagado), 
      aplicar_saldo: usarSaldo,
      monto_esperado_nuevo: parseFloat(montoEsperado) // Enviamos el valor de la logia para crearlo
    });
  };

  return (
    <div className="fixed inset-0 bg-[#050505]/80 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-[#C5A059]/30 rounded-none w-full max-w-md p-8 shadow-2xl relative">
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent opacity-50"></div>
        <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
          <h2 className="font-serif text-xl text-white m-0">Procesar Pago</h2>
          <button onClick={onClose} className="bg-transparent border-none text-gray-500 hover:text-white transition-colors cursor-pointer p-1"><X size={20} /></button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="bg-white/5 border border-white/10 p-4 rounded-sm flex flex-col gap-2">
            <span className="text-gray-400 text-xs">Hermano: <strong className="text-white">{data.hermano.nombre}</strong></span>
            <span className="text-gray-400 text-xs">Concepto: <strong className="text-white uppercase">{data.concepto} {data.mes ? `(Mes ${data.mes})` : ''}</strong></span>
            
            {noExisteDeuda ? (
              <div className="flex flex-col gap-1 mt-2 p-2 bg-[#111111] border border-[#C5A059]/30">
                <label className="text-[#C5A059] text-[9px] tracking-widest uppercase">Establecer costo para esta Logia (S/.)</label>
                <input 
                  type="number" step="0.01" required 
                  placeholder="Ej. 150.00" 
                  value={montoEsperado} 
                  onChange={(e) => setMontoEsperado(e.target.value)} 
                  className="bg-transparent border-none text-white text-sm focus:outline-none font-mono" 
                />
              </div>
            ) : (
              <span className="text-[#C5A059] text-xs font-mono">Cuota a cubrir: S/ {parseFloat(montoEsperado).toFixed(2)}</span>
            )}

            {data.hermano.saldo_favor > 0 && (
              <span className="text-green-400 text-xs font-mono mt-1">Saldo a favor disponible: S/ {data.hermano.saldo_favor.toFixed(2)}</span>
            )}
          </div>
          
          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Monto Entregado por H:. (S/.)</label>
            <input 
              type="number" step="0.01" required 
              value={montoPagado} 
              onChange={(e) => setMontoPagado(e.target.value)} 
              className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" 
            />
            <p className="text-[9px] text-gray-500 uppercase tracking-widest mt-1">Si el monto supera la cuota, el excedente irá al saldo a favor.</p>
          </div>

          {data.hermano.saldo_favor > 0 && (
            <label className="flex items-center gap-3 cursor-pointer mt-2">
              <input type="checkbox" checked={usarSaldo} onChange={(e) => setUsarSaldo(e.target.checked)} className="accent-[#C5A059] w-4 h-4 cursor-pointer" />
              <span className="text-gray-300 text-xs">Aplicar mi saldo a favor para completar este pago</span>
            </label>
          )}

          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} className="bg-transparent border border-white/20 text-gray-300 px-6 py-3 text-xs tracking-widest uppercase hover:bg-white/5 transition-colors">Cancelar</button>
            <button type="submit" className="bg-[#C5A059] hover:bg-white text-[#050505] border-none px-6 py-3 text-xs tracking-widest uppercase font-bold transition-colors">Procesar Pago</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GestionarCuotasModal({ onClose, onSave }) {
  const [concepto, setConcepto] = useState('extraordinaria');
  const [montoEsperado, setMontoEsperado] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ concepto, monto_esperado: parseFloat(montoEsperado) });
  };

  return (
    <div className="fixed inset-0 bg-[#050505]/80 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-[#C5A059]/30 rounded-none w-full max-w-md p-8 shadow-2xl relative">
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent opacity-50"></div>
        <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
          <h2 className="font-serif text-2xl text-white m-0">Generar Cuota Masiva</h2>
          <button onClick={onClose} className="bg-transparent border-none text-gray-500 hover:text-white transition-colors cursor-pointer p-1"><X size={20} /></button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <p className="text-gray-400 text-xs font-light">Esta acción generará una deuda para todos los miembros activos de la logia en el concepto seleccionado.</p>
          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Concepto de Evento</label>
            <select value={concepto} onChange={(e) => setConcepto(e.target.value)} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059] appearance-none cursor-pointer">
              <option value="extraordinaria">Cuota Extraordinaria</option>
              <option value="aniversario">Cuota de Aniversario</option>
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Monto Obligatorio (S/.)</label>
            <input type="number" step="0.01" required value={montoEsperado} onChange={(e) => setMontoEsperado(e.target.value)} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
          </div>
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} className="bg-transparent border border-white/20 text-gray-300 px-6 py-3 text-xs tracking-widest uppercase hover:bg-white/5 transition-colors">Cancelar</button>
            <button type="submit" className="bg-[#C5A059] hover:bg-white text-[#050505] border-none px-6 py-3 text-xs tracking-widest uppercase font-bold transition-colors">Generar Deuda</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NuevoMovimientoModal({ onClose, onSave }) {
  const [tipo, setTipo] = useState('ingreso');
  const [hermano, setHermano] = useState('');
  const [detalle, setDetalle] = useState('');
  const [monto, setMonto] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ tipo, hermano, detalle, monto: parseFloat(monto) });
  };

  return (
    <div className="fixed inset-0 bg-[#050505]/80 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-[#C5A059]/30 rounded-none w-full max-w-lg p-8 shadow-2xl relative">
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent opacity-50"></div>
        <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
          <h2 className="font-serif text-2xl text-white m-0">Registrar Movimiento Libre</h2>
          <button onClick={onClose} className="bg-transparent border-none text-gray-500 hover:text-white transition-colors cursor-pointer p-1"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Tipo</label>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059] appearance-none cursor-pointer">
                <option value="ingreso">Ingreso Libre</option>
                <option value="egreso">Egreso (Gasto)</option>
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Monto (S/.)</label>
              <input type="number" step="0.01" required placeholder="0.00" value={monto} onChange={(e) => setMonto(e.target.value)} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Hermano o Proveedor (Opcional)</label>
            <input type="text" placeholder="Ej. Juan Pérez" value={hermano} onChange={(e) => setHermano(e.target.value)} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Detalle</label>
            <input type="text" required placeholder="Ej. Compra de suministros" value={detalle} onChange={(e) => setDetalle(e.target.value)} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
          </div>
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} className="bg-transparent border border-white/20 text-gray-300 px-6 py-3 text-xs tracking-widest uppercase hover:bg-white/5 transition-colors">Cancelar</button>
            <button type="submit" className="bg-[#C5A059] hover:bg-white text-[#050505] border-none px-6 py-3 text-xs tracking-widest uppercase font-bold transition-colors">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  );
}